// ============================================================
// SERIESPILL (LEAGUE-PLAY) ENGINE — moved out of the legacy monolith
// (Phase 3, incremental split).
//
// Pure, self-contained league-play engine: config/defaults, the per-division
// round-robin match builder (buildSeriespillDivisionMatches), the configurable
// standings calculator (calcSeriespillStandings), promotion/relegation
// (computeSeriespillPromotion + seriespillDefaultUpDown), the main tournament
// factory (createSeriespillTournament), the full season life-cycle
// (shouldLockSeason / lockSeasonIfDone / proposeSeasonTransition /
// startNextSeason / season-deadline + rolling-reveal helpers), data repair, the
// shared slot engine (serieGenerateSlots / serieBuildReservations /
// serieAssignMatchesToSlots), and the pure match-court/date/time accessors
// (serieMatchCourt/Date/Start/End — banepad reuses these).
//
// No storage, no React, no ELO. The seriespillEngine wiring object +
// Padel24Engine.seriespill assignment now live in src/ (mirrors the
// booking/KM precedent). applySerieResult also lives elsewhere now — it calls
// recalcKMElo, a KM function that was once interleaved among the React
// components in the monolith. This module must NEVER import from the legacy
// monolith.
//
// calcSeriespillStandings + createSeriespillTournament are pinned by
// tests/standings.test.ts — keep them behaviorally identical (byte-for-byte).
//
// Seriespill reuses the KM match-format constants/seeding/round-robin and the
// booking validation/recurrence helpers — imported from the already-extracted
// src/ modules below.
//
// The inline comments below have been translated from the original Norwegian
// (bokmål); loosely-shaped tournament / match / division / pair objects are
// typed as `any` on purpose.
// ============================================================

import {
  KM_DEFAULT_GROUP_MATCH_FORMAT,
  KM_DEFAULT_GROUP_MINUTES,
  roundRobinSchedule,
  buildPairsFromPlayers,
  sortPairsBySeed,
} from './km.js';
import {
  BOOKING_DEFAULT_BUFFER_MIN,
  validateBooking,
  isHoliday,
  expandRecurrence,
} from './booking.js';

// ============================================================
// SERIESPILL (LEAGUE) — ENGINE
// Unit = fixed pairs (like KM). Team strength = average ELO. Round-robin per
// division: every pair plays every other. NO playoffs, NO banepad. Built for
// maximal reuse of KM: the match objects have the SAME field structure as KM
// group matches (team1/team2: { pairId, p1Id, p2Id }, score, playerEloPre,
// matchFormat) so that KMScoreModal, recalcKMElo and the standings views work
// unchanged.
//
// CRITICAL (HANDOVER §13): the division ID must be globally unique. Seriespill
// has no sub-groups — the DIVISION is the competition unit. We therefore ALWAYS
// match on divisionId. groupId is set equal to divisionId for display reuse, but
// is never a match key on its own.
// ============================================================

// Default values for seriespill (reuses KM's format constants)
export const SERIESPILL_DEFAULT_MATCH_FORMAT = KM_DEFAULT_GROUP_MATCH_FORMAT;
export const SERIESPILL_DEFAULT_MINUTES = KM_DEFAULT_GROUP_MINUTES;
export const SERIESPILL_DEFAULT_WIN_POINTS = 3; // standard padel league: 3 for a win
// Color palette for round banners — each round gets its own color (cyclic).
export const SERIE_ROUND_PALETTE = [
  { bg: '#eef2ff', accent: '#4f46e5' }, // indigo
  { bg: '#ecfeff', accent: '#0e7490' }, // cyan
  { bg: '#f0fdf4', accent: '#15803d' }, // green
  { bg: '#fff7ed', accent: '#c2410c' }, // orange
  { bg: '#fdf4ff', accent: '#a21caf' }, // fuchsia
  { bg: '#fef2f2', accent: '#be123c' }, // rose
  { bg: '#f0f9ff', accent: '#0369a1' }, // sky
  { bg: '#fefce8', accent: '#a16207' }, // gold
];
export const SERIESPILL_DEFAULT_LOSS_POINTS = 0; // 0 for a loss (can't be a draw in best of 3)
export const SERIESPILL_DEFAULT_PROMOTE = 2; // 2 up by default (adjustable per division)
export const SERIESPILL_DEFAULT_RELEGATE = 2; // 2 down by default

// ---------- Build league matches for ONE division ----------
// Reuses roundRobinSchedule (circle method). For a double league the rounds are
// mirrored with home/away swapped, and round numbering continues (leg 2).
export function buildSeriespillDivisionMatches(
  division,
  divPairs,
  { doubleRound = false, matchFormat = null } = {},
) {
  const baseRounds = roundRobinSchedule(divPairs); // [[{p1,p2},...], ...]
  const allRounds = doubleRound
    ? [...baseRounds, ...baseRounds.map((rd) => rd.map((m) => ({ p1: m.p2, p2: m.p1 })))]
    : baseRounds;
  const firstLegRoundCount = baseRounds.length;

  const matches = [];
  allRounds.forEach((round, ri) => {
    round.forEach((m, mi) => {
      matches.push({
        id: `serie_${division.id}_r${ri + 1}_m${mi + 1}`,
        phase: 'league', // own phase — not 'group'/'knockout'
        classId: division.classId,
        divisionId: division.id,
        groupId: division.id, // the division IS the "group"; always match on divisionId
        groupName: division.name,
        round: ri + 1, // round number in the league (shown to the players)
        roundIndex: ri + 1,
        leg: doubleRound && ri >= firstLegRoundCount ? 2 : 1, // 1=home round, 2=return
        team1: { pairId: m.p1.id, p1Id: m.p1.p1Id, p2Id: m.p1.p2Id },
        team2: { pairId: m.p2.id, p1Id: m.p2.p1Id, p2Id: m.p2.p2Id },
        court: null,
        date: null,
        startTime: null,
        endTime: null,
        score: null,
        completed: false,
        playerEloPre: null, // set by recalcKMElo (reused) when a result is entered
        matchFormat: matchFormat || division.matchFormat || SERIESPILL_DEFAULT_MATCH_FORMAT,
        minutesPerMatch: division.minutesPerMatch || SERIESPILL_DEFAULT_MINUTES,
      });
    });
  });
  return matches;
}

// ---------- Standings/leaderboard for one division ----------
// Generalized version of calcGroupStandings: configurable points + WO handling.
// Same tiebreak order as KM: points → head-to-head → set diff → game diff → games → seed.
// Verified in a Node prototype (/tmp/seriespill_proto.js, 40/40).
export function calcSeriespillStandings(divPairs, matches, opts: any = {}) {
  const winPoints = opts.winPoints ?? SERIESPILL_DEFAULT_WIN_POINTS;
  const lossPoints = opts.lossPoints ?? SERIESPILL_DEFAULT_LOSS_POINTS;
  const woPoints = opts.woPoints ?? winPoints; // a WO win counts as a normal win in the table

  const stats: any = {};
  (divPairs || []).forEach((p) => {
    stats[p.id] = {
      pair: p,
      played: 0,
      wins: 0,
      losses: 0,
      draws: 0,
      wo: 0,
      points: 0,
      setsFor: 0,
      setsAgainst: 0,
      setDiff: 0,
      gamesFor: 0,
      gamesAgainst: 0,
      gameDiff: 0,
      headToHead: {},
    };
  });

  (matches || []).forEach((m) => {
    if (!m?.score || m.score.t1 == null || m.score.t2 == null) return;
    const p1Id = m.team1?.pairId,
      p2Id = m.team2?.pairId;
    if (!p1Id || !p2Id || !stats[p1Id] || !stats[p2Id]) return;
    const s1 = stats[p1Id],
      s2 = stats[p2Id];
    // Double WO (unplayed match when a round is closed): both teams lose 0–2.
    // No winner, loss points to both, and 0–2 in sets against each.
    if (m.score.doubleWalkover) {
      s1.played++;
      s2.played++;
      s1.losses++;
      s2.losses++;
      s1.wo++;
      s2.wo++;
      s1.points += lossPoints;
      s2.points += lossPoints;
      s1.setsAgainst += 2;
      s2.setsAgainst += 2;
      s1.headToHead[p2Id] = { result: 'loss' };
      s2.headToHead[p1Id] = { result: 'loss' };
      return;
    }
    // Draw (voided match when a round is closed): counts as played, 0 points
    // to each, no win/loss. Kept out of ELO (walkover flag).
    if (m.score.draw) {
      s1.played++;
      s2.played++;
      s1.draws++;
      s2.draws++;
      s1.setsFor += m.score.t1;
      s1.setsAgainst += m.score.t2;
      s2.setsFor += m.score.t2;
      s2.setsAgainst += m.score.t1;
      s1.headToHead[p2Id] = { result: 'draw' };
      s2.headToHead[p1Id] = { result: 'draw' };
      return;
    }
    s1.played++;
    s2.played++;
    s1.setsFor += m.score.t1;
    s1.setsAgainst += m.score.t2;
    s2.setsFor += m.score.t2;
    s2.setsAgainst += m.score.t1;
    if (Array.isArray(m.score.sets)) {
      m.score.sets.forEach((set) => {
        s1.gamesFor += set?.t1 || 0;
        s1.gamesAgainst += set?.t2 || 0;
        s2.gamesFor += set?.t2 || 0;
        s2.gamesAgainst += set?.t1 || 0;
      });
    }
    const isWo = !!m.score.walkover;
    if (m.score.t1 > m.score.t2) {
      s1.wins++;
      s1.points += isWo ? woPoints : winPoints;
      if (isWo) s1.wo++;
      s2.losses++;
      s2.points += lossPoints;
      s1.headToHead[p2Id] = { result: 'win' };
      s2.headToHead[p1Id] = { result: 'loss' };
    } else if (m.score.t2 > m.score.t1) {
      s2.wins++;
      s2.points += isWo ? woPoints : winPoints;
      if (isWo) s2.wo++;
      s1.losses++;
      s1.points += lossPoints;
      s2.headToHead[p1Id] = { result: 'win' };
      s1.headToHead[p2Id] = { result: 'loss' };
    }
  });

  Object.values(stats).forEach((s: any) => {
    s.setDiff = s.setsFor - s.setsAgainst;
    s.gameDiff = s.gamesFor - s.gamesAgainst;
  });

  const compareBy = (id, a, b) => {
    switch (id) {
      case 'wins':
        return b.points - a.points || b.wins - a.wins;
      case 'setsDiff':
        return b.setDiff - a.setDiff;
      case 'gamesDiff':
        return b.gameDiff - a.gameDiff;
      case 'gamesWon':
        return b.gamesFor - a.gamesFor;
      case 'h2h': {
        const h = a.headToHead?.[b.pair.id];
        if (h?.result === 'win') return -1;
        if (h?.result === 'loss') return 1;
        return 0;
      }
      default:
        return 0;
    }
  };
  const order = [
    { id: 'wins' },
    { id: 'h2h' },
    { id: 'setsDiff' },
    { id: 'gamesDiff' },
    { id: 'gamesWon' },
  ];
  const sorted = Object.values(stats).sort((a: any, b: any) => {
    for (const crit of order) {
      const d = compareBy(crit.id, a, b);
      if (d !== 0) return d;
    }
    return (a.pair?.seed || 999) - (b.pair?.seed || 999);
  });
  return sorted.map((s: any, i: number) => ({ ...s, pos: i + 1 }));
}

// ---------- Promotion/relegation proposal ----------
// divisions: [{ id, level (1=top), standings:[...], promote, relegate }]
// Top division: no promotion. Bottom division: no relegation. Verified in Node.
// Default promotion/relegation count based on division size:
//   ≤4 teams → 1, 5–9 → 2, 10+ → 3. Never more than the division can take (size − 1).
// Admin can override per division (autoUpDown=false + own promote/relegate counts).
export function seriespillDefaultUpDown(size) {
  const n = size || 0;
  const c = n <= 4 ? 1 : n <= 9 ? 2 : 3;
  return Math.min(c, Math.max(0, n - 1));
}

export function computeSeriespillPromotion(divisions) {
  const sorted = [...(divisions || [])].sort((a, b) => (a.level || 0) - (b.level || 0));
  return sorted.map((div, idx) => {
    const isTop = idx === 0;
    const isBottom = idx === sorted.length - 1;
    const st = div.standings || [];
    const n = st.length;
    // Auto (default): compute from the number of teams. Manual: use stored counts.
    const auto = div.autoUpDown !== false;
    const basePromote = auto
      ? seriespillDefaultUpDown(n)
      : (div.promote ?? SERIESPILL_DEFAULT_PROMOTE);
    const baseRelegate = auto
      ? seriespillDefaultUpDown(n)
      : (div.relegate ?? SERIESPILL_DEFAULT_RELEGATE);
    const promote = isTop ? 0 : basePromote;
    const relegate = isBottom ? 0 : baseRelegate;
    const goingUp = isTop
      ? []
      : st
          .slice(0, promote)
          .map((s) => s.pair?.id)
          .filter(Boolean);
    const goingDown = isBottom
      ? []
      : st
          .slice(Math.max(0, n - relegate))
          .map((s) => s.pair?.id)
          .filter(Boolean);
    return { divisionId: div.id, level: div.level, goingUp, goingDown };
  });
}

// ---------- Main factory: create a seriespill tournament ----------
// Builds class → division → pairs → league matches. Each division is one round-robin.
export function createSeriespillTournament({
  name = 'Seriespill',
  classes, // [{ id, name, gender, divisions: [{ id, name, level, promote, relegate, starpoint, pairs:[{p1Id,p2Id}], matchFormat, minutesPerMatch }] }]
  courtMode = 'unallocated', // 'unallocated' (the pairs book themselves) | 'allocated' (courts on fixed days)
  courts = 4,
  days = [], // used only with courtMode 'allocated'
  slotPlan = null, // (legacy) single slot plan — migrated into one room
  slotRooms = null, // allocated: [{ id, name, weekdays:[0-6], startTime, endTime, courts, classIds:[] }]
  slotDuration = 90, // shared slot duration (90|120) for all rooms
  deadline = null, // season deadline (ISO date) — the whole league must be finished
  seasonStart = null, // season start (ISO date) — basis for round deadlines
  roundInterval = null, // rolling round deadline: { value: N, unit: 'weeks'|'months' } | null
  doubleRound = false, // single (false) or double/home-away (true)
  winPoints = SERIESPILL_DEFAULT_WIN_POINTS,
  lossPoints = SERIESPILL_DEFAULT_LOSS_POINTS,
  woPoints = null, // null = same as winPoints
  unplayedPolicy = 'double_wo', // unplayed matches when a round is closed: 'double_wo' (both lose 0–2) | 'void' (voided)
  autoForfeitOverdue = false, // close a round automatically when its deadline passes
  revealMode = 'rolling', // default: announced just before start | 'all' = all rounds visible
  revealAhead = 1, // how many rounds ahead are revealed (1+)
  revealLeadValue = 0, // lead time before round start
  revealLeadUnit = 'days', // 'days' | 'weeks'
  kMultiplier = 2.0, // ELO weight 200% for seriespill (full season, weighted heaviest)
  matchFormat = SERIESPILL_DEFAULT_MATCH_FORMAT, // league-standard match format
  goldenPoint = false, // (legacy) league standard: golden point at 40-40
  deuceMode = null, // league-standard scoring mode: 'advantage'|'golden'|'starpoint'
  mixServeRule = false, // league standard: in Mix, woman serves to woman / man to man
  rulesNote = '', // league-level main note (free text) for rules
  allPlayers = [],
  localPlayers = [], // demo/local players — stored on the league, NEVER in the global DB
}) {
  if (!Array.isArray(classes) || classes.length === 0) {
    throw new Error('createSeriespillTournament krever minst én klasse');
  }

  const tournament = {
    id: `serie_${Date.now()}`,
    type: 'seriespill',
    name,
    config: {
      tournamentType: 'seriespill',
      name,
      classes: [],
      courtMode,
      courts,
      days,
      slotPlan,
      slotRooms:
        slotRooms ||
        (slotPlan
          ? [
              {
                id: `room_${Date.now()}`,
                name: 'Tidsrom 1',
                weekdays: slotPlan.weekdays || [],
                startTime: slotPlan.startTime,
                endTime: slotPlan.endTime,
                courts: slotPlan.courts,
                classIds: [],
              },
            ]
          : []),
      slotDuration: slotDuration || slotPlan?.durationMin || 90,
      deadline,
      seasonStart,
      roundInterval,
      doubleRound,
      winPoints,
      lossPoints,
      woPoints,
      unplayedPolicy,
      autoForfeitOverdue,
      revealMode,
      revealAhead,
      revealLeadValue,
      revealLeadUnit,
      kMultiplier,
      matchFormat,
      deuceMode: deuceMode || (goldenPoint ? 'golden' : 'advantage'),
      goldenPoint: (deuceMode || (goldenPoint ? 'golden' : 'advantage')) === 'golden',
      mixServeRule,
      rulesNote,
    },
    pairs: [],
    matches: [],
    localPlayers: [...localPlayers],
    status: 'setup',
    // ── Season track ──────────────────────────────────────────────────────────
    // The league lives on; seasons roll. currentSeason increments at a season transition.
    // seasonStatus: 'active' = play in progress, 'locked' = a trigger fired, waiting on
    // admin confirmation to start the next season. seriesHistory stores frozen
    // standings + promotion/relegation for each finished season (for display + merits).
    // autoAdvance: if true, the next season starts automatically without admin confirmation
    // (the engine uses the default plan with standard promotion/relegation).
    currentSeason: 1,
    seasonStatus: 'active',
    seriesHistory: [],
    autoAdvance: false,
    createdAt: new Date().toISOString(),
  };

  classes.forEach((cls) => {
    // Per-class season fields. These control the class's own season life-cycle.
    // The default follows the league-level fields; admin can override per class
    // afterwards via the «Per-klasse innstillinger» panel in SeriespillView. Each
    // class has its own currentSeason counter and can be locked / start a new
    // season independently.
    const classOut = {
      id: cls.id,
      name: cls.name,
      gender: cls.gender,
      // Season life-cycle per class (backwards-compat: read from league level if empty)
      currentSeason: cls.currentSeason || 1,
      seasonStatus: cls.seasonStatus || 'active',
      seasonStart: cls.seasonStart || null, // null = follow league level
      deadline: cls.deadline || null, // null = follow league level
      roundInterval: cls.roundInterval || null, // null = follow league level
      doubleRound: cls.doubleRound != null ? cls.doubleRound : null, // null = follow league level
      // Rule overrides at class level (null = inherit from league)
      matchFormat: cls.matchFormat ?? null,
      goldenPoint: cls.goldenPoint ?? null,
      mixServeRule: cls.mixServeRule ?? null,
      lockedAt: null,
      lockReason: null,
      seriesHistory: [],
      divisions: [],
    };

    // Sort divisions by level (1 = top) for consistent promotion/relegation
    const divsSorted = [...(cls.divisions || [])].sort((a, b) => (a.level || 0) - (b.level || 0));

    divsSorted.forEach((div, divIdx) => {
      // Globally unique division id from day one (HANDOVER §13). Keep the existing
      // id if set, otherwise derive it from class + level.
      const divisionId = div.id || `${cls.id}_div${div.level || divIdx + 1}`;

      const builtPairs = buildPairsFromPlayers(
        (div.pairs || []).map((p) => ({ ...p, classId: cls.id, divisionId })),
        allPlayers,
      );
      // Seed = strength by average ELO (eloSum gives the same order for equal pair sizes)
      const sortedPairs = sortPairsBySeed(builtPairs).map((p, i) => ({
        ...p,
        seed: p.seedOverride ?? i + 1,
        groupId: divisionId,
      }));

      // Effective rules for the division (league → class → division).
      const effRules = seriespillEffectiveRules(
        { matchFormat, goldenPoint, mixServeRule },
        {
          matchFormat: cls.matchFormat ?? null,
          goldenPoint: cls.goldenPoint ?? null,
          mixServeRule: cls.mixServeRule ?? null,
        },
        div,
      );

      const divisionMeta = {
        id: divisionId,
        classId: cls.id,
        name: div.name || `${div.level || divIdx + 1}. divisjon`,
        level: div.level || divIdx + 1,
        promote: div.promote ?? SERIESPILL_DEFAULT_PROMOTE,
        relegate: div.relegate ?? SERIESPILL_DEFAULT_RELEGATE,
        autoUpDown: div.autoUpDown !== false,
        // Rule overrides (null = inherit from class/league); starpoint is kept for
        // backwards-compat and set to the effective golden-point value.
        matchFormat: div.matchFormat ?? null,
        goldenPoint: div.goldenPoint ?? null,
        mixServeRule: div.mixServeRule ?? null,
        rulesNote: div.rulesNote || '',
        starpoint: effRules.goldenPoint,
        minutesPerMatch: div.minutesPerMatch || SERIESPILL_DEFAULT_MINUTES,
        pairs: sortedPairs,
      };

      // Build league matches only if there are at least 2 pairs. Empty divisions
      // (without players) are allowed — admin can add pairs later and generate
      // matches then. This makes it possible to create the skeleton of a league
      // without having all registrations ready.
      const divMatches =
        sortedPairs.length >= 2
          ? buildSeriespillDivisionMatches(divisionMeta, sortedPairs, {
              doubleRound: cls.doubleRound != null ? cls.doubleRound : doubleRound,
              matchFormat: effRules.matchFormat,
            })
          : [];

      classOut.divisions.push(divisionMeta);
      tournament.pairs.push(...sortedPairs);
      tournament.matches.push(...divMatches);
    });

    tournament.config.classes.push(classOut);
  });

  return tournament;
}

// ---------- Round deadlines (rolling cadence) ----------
// roundInterval: { value, unit: 'weeks'|'months' }. Round R's deadline =
// seasonStart + R * interval. Returns a Date or null if not configured.
export function seriespillRoundDeadline(config, roundNumber) {
  // A per-round override (manually set deadline) takes precedence over the computed cadence.
  const ov = config?.roundDeadlines && config.roundDeadlines[roundNumber];
  if (ov) {
    const od = new Date(ov);
    if (!isNaN(od as any)) return od;
  }
  const ri = config?.roundInterval;
  const startStr = config?.seasonStart;
  if (!ri || !ri.value || !startStr) return null;
  const start = new Date(startStr);
  if (isNaN(start as any)) return null;
  const d = new Date(start);
  const n = ri.value * roundNumber; // deadline AFTER the round has been played out
  if (ri.unit === 'months') d.setMonth(d.getMonth() + n);
  else d.setDate(d.getDate() + n * 7); // weeks
  return d;
}

// Which round is "active" today? Simulates a new round opening when the previous
// round's deadline passes (the client side has no cron — we compute it at
// render time). Active round = number of passed round intervals + 1, clamped to
// [1, maxRound]. Without a cadence, the lowest round with unplayed matches is
// treated as active.
// Max number of rounds in a set of divisions (round-robin: n-1 if an even number
// of pairs, otherwise n; ×2 for a double league). Used to estimate the season
// end from the round deadline.
export function serieMaxRounds(divisions, doubleRound) {
  let mx = 0;
  (divisions || []).forEach((d) => {
    const n = (d.pairs || []).length;
    if (n >= 2) {
      let r = n % 2 === 0 ? n - 1 : n;
      if (doubleRound) r *= 2;
      mx = Math.max(mx, r);
    }
  });
  return mx;
}
// Estimated season end = the deadline of the last round given cadence + max rounds.
export function serieEstSeasonEnd(seasonStart, riValue, riUnit, divisions, doubleRound) {
  const v = Number(riValue);
  if (!(v > 0) || !seasonStart) return null;
  const maxR = serieMaxRounds(divisions, doubleRound);
  if (maxR === 0) return null;
  return seriespillRoundDeadline({ seasonStart, roundInterval: { value: v, unit: riUnit } }, maxR);
}

export function seriespillActiveRound(config, maxRound, now = Date.now()) {
  const ri = config?.roundInterval;
  const startStr = config?.seasonStart;
  if (!ri || !ri.value || !startStr || !maxRound) return 1;
  const start = new Date(startStr);
  if (isNaN(start as any)) return 1;
  let passed = 0;
  for (let r = 1; r <= maxRound; r++) {
    const dl = seriespillRoundDeadline(config, r - 1); // opening time for round r
    if (dl && dl.getTime() <= now) passed = r;
  }
  return Math.min(maxRound, Math.max(1, passed || 1));
}

// ============================================================
// SEASON LIFE-CYCLE
// The league lives on; seasons roll. Two triggers lock a season:
//   (a) all matches in all divisions are played, OR
//   (b) the season deadline has passed.
// Lock = stops further play, waits for admin confirmation. Admin calls
// startNextSeason() with a possibly adjusted placement (withdrawals, manual
// adjustments, rarely new pairs via override). Then promotion/relegation is
// computed, the previous season is archived on seriesHistory[], and a new
// round-robin is generated.
// ============================================================

// Should the season be locked NOW? Returns { lock: bool, reason: 'completed'|'deadline'|null }
// Helper: get the effective setting for a class (class override → league default).
// Used for seasonStart, deadline, roundInterval, doubleRound.
export function getClassSetting(series, classId, field) {
  const cls = (series?.config?.classes || []).find((c) => c.id === classId);
  if (!cls) return series?.config?.[field] ?? null;
  // null / undefined at class level means «follow league level»
  if (cls[field] != null) return cls[field];
  return series?.config?.[field] ?? null;
}

// Fields a class can override. The main settings are the «template»; class
// values (≠ null) win. Add new overridable fields here and they inherit the
// mechanism.
export const SERIE_OVERRIDABLE_FIELDS = [
  'seasonStart',
  'deadline',
  'roundInterval',
  'doubleRound',
  'courtMode',
  'matchFormat',
  'goldenPoint',
  'deuceMode',
  'mixServeRule',
  'roundDeadlines',
  'winPoints',
  'lossPoints',
  'woPoints',
  'unplayedPolicy',
  'autoForfeitOverdue',
  'revealMode',
  'revealAhead',
  'revealLeadValue',
  'revealLeadUnit',
  'kMultiplier',
  'rulesNote',
  'manualReveals',
];

// Effective config for a class: the league config (template) overlaid with the
// class's overrides. Used everywhere behavior is class-specific (deadlines,
// announcement, forfeit, points). One place to resolve = no scattered
// inheritance logic.
export function seriespillEffectiveConfig(series, classId) {
  const base = { ...(series?.config || {}) };
  const cls = (series?.config?.classes || []).find((c) => c.id === classId);
  if (!cls) return base;
  SERIE_OVERRIDABLE_FIELDS.forEach((f) => {
    if (cls[f] != null) base[f] = cls[f];
  });
  return base;
}

// Should a SPECIFIC CLASS season be locked NOW? Classes have their own triggers/rules.
// Returns { lock: bool, reason: 'completed'|'deadline'|null }
export function shouldLockSeason(series, classIdOrNull = null, now = Date.now()) {
  if (!series) return { lock: false, reason: null };
  // Backwards-compat: if classId is not given, we return the league-level status
  // (legacy behavior). The recommendation is to always pass classId.
  if (!classIdOrNull) {
    if (series.seasonStatus === 'locked') return { lock: false, reason: null };
    const allDone =
      (series.matches || []).length > 0 && (series.matches || []).every((m) => m && m.score);
    if (allDone) return { lock: true, reason: 'completed' };
    const dl = series.config?.deadline ? new Date(series.config.deadline).getTime() : null;
    if (dl && now >= dl) return { lock: true, reason: 'deadline' };
    return { lock: false, reason: null };
  }
  const cls = (series.config?.classes || []).find((c) => c.id === classIdOrNull);
  if (!cls || cls.seasonStatus === 'locked') return { lock: false, reason: null };
  // Get the matches that belong to this class
  const divIds = new Set((cls.divisions || []).map((d) => d.id));
  const classMatches = (series.matches || []).filter((m) => divIds.has(m.divisionId));
  const allDone = classMatches.length > 0 && classMatches.every((m) => m && m.score);
  if (allDone) return { lock: true, reason: 'completed' };
  // Effective deadline (class override → league default)
  const effectiveDeadline = cls.deadline != null ? cls.deadline : series.config?.deadline;
  const dl = effectiveDeadline ? new Date(effectiveDeadline).getTime() : null;
  if (dl && now >= dl) return { lock: true, reason: 'deadline' };
  return { lock: false, reason: null };
}

// Lock the season for classes if triggers fire. Idempotent. Checks all classes
// if classIdOrNull = null. Returns a new series.
export function lockSeasonIfDone(series, classIdOrNull = null, now = Date.now()) {
  if (!series) return series;
  const classes = series.config?.classes || [];
  if (classes.length === 0) return series;

  // If a specific class is selected: lock only that one
  if (classIdOrNull) {
    const { lock, reason } = shouldLockSeason(series, classIdOrNull, now);
    if (!lock) return series;
    const nextClasses = classes.map((c) =>
      c.id === classIdOrNull
        ? { ...c, seasonStatus: 'locked', lockedAt: now, lockReason: reason }
        : c,
    );
    return { ...series, config: { ...series.config, classes: nextClasses } };
  }

  // Otherwise: check ALL classes at once
  let changed = false;
  const nextClasses = classes.map((c) => {
    const { lock, reason } = shouldLockSeason(series, c.id, now);
    if (lock) {
      changed = true;
      return { ...c, seasonStatus: 'locked', lockedAt: now, lockReason: reason };
    }
    return c;
  });
  if (!changed) return series;
  // If ALL classes are locked, also set the league-level status (for backwards-compat UI)
  const allLocked = nextClasses.every((c) => c.seasonStatus === 'locked');
  return {
    ...series,
    config: { ...series.config, classes: nextClasses },
    ...(allLocked ? { seasonStatus: 'locked', lockedAt: now, lockReason: 'completed' } : {}),
  };
}

// Standings per division for the whole league — used by season transition + UI.
export function seriespillStandingsByDivision(series) {
  const out = {};
  (series?.config?.classes || []).forEach((cls) => {
    (cls.divisions || []).forEach((div) => {
      const dp = (series.pairs || []).filter((p) => p.divisionId === div.id);
      const dm = (series.matches || []).filter((m) => m.divisionId === div.id);
      out[div.id] = calcSeriespillStandings(dp, dm, {
        winPoints: series?.config?.winPoints,
        lossPoints: series?.config?.lossPoints,
        woPoints: series?.config?.woPoints,
      });
    });
  });
  return out;
}

// Propose a promotion/relegation plan for one class (or all).
export function proposeSeasonTransition(series, classIdOrNull = null) {
  const stByDiv = seriespillStandingsByDivision(series);
  const plan = {};
  const classes = (series?.config?.classes || []).filter(
    (cls) => !classIdOrNull || cls.id === classIdOrNull,
  );
  classes.forEach((cls) => {
    const divs = [...(cls.divisions || [])].sort((a, b) => (a.level || 0) - (b.level || 0));
    const enriched = divs.map((d) => ({
      id: d.id,
      level: d.level || 0,
      name: d.name,
      promote: d.promote ?? SERIESPILL_DEFAULT_PROMOTE,
      relegate: d.relegate ?? SERIESPILL_DEFAULT_RELEGATE,
      standings: stByDiv[d.id] || [],
    }));
    const promo = computeSeriespillPromotion(enriched);
    plan[cls.id] = {
      divisions: enriched.map((d, i) => ({
        ...d,
        goingUp: promo[i].goingUp,
        goingDown: promo[i].goingDown,
      })),
    };
  });
  return plan;
}

// Build the next season's divisions based on a concrete placement. assignments is
// the administrator's (possibly adjusted) plan: { [classId]: { [divisionId]: [pairId, ...] } }.
// Returns new division objects ready for buildSeriespillDivisionMatches.
export function applySeasonAssignments(series, assignments) {
  const pairById = new Map<any, any>((series?.pairs || []).map((p) => [p.id, p]));
  const nextClasses = (series?.config?.classes || []).map((cls) => {
    const divs = [...(cls.divisions || [])].sort((a, b) => (a.level || 0) - (b.level || 0));
    const assignForClass = assignments?.[cls.id] || {};
    const nextDivs = divs.map((d) => {
      const pairIds =
        assignForClass[d.id] ||
        (d.pairs || []).map((_p) => {
          // Fallback: keep the pair objects that were in the division (best effort)
          const pid = `${d.id}_pair_unknown`;
          return pid;
        });
      const nextPairs = pairIds
        .map((pid) => {
          const p = pairById.get(pid);
          return p ? { p1Id: p.p1Id, p2Id: p.p2Id } : null;
        })
        .filter(Boolean);
      return { ...d, pairs: nextPairs };
    });
    return { ...cls, divisions: nextDivs };
  });
  return nextClasses;
}

// Start the next season for ONE CLASS (class-wise transition). Archives the
// class's season standings + transition on classes[i].seriesHistory, builds new
// divisions from assignments, and generates a new round-robin for the class.
// Other classes are left untouched.
// ---------- Data repair: make all pair ids globally unique ----------
// Older/test data may have pairs that share an id across (or within) divisions,
// which corrupts season transitions, promotion/relegation badges and team
// filters. This gives each logical pair (division + player pair) a fresh unique
// id, removes real duplicates, and re-links all matches via the players (not the
// old ids).
// Returns { series, report } — report = { pairsBefore, pairsAfter, duplicatesRemoved, idsReassigned, matchesRelinked }.
export function repairSeriespillPairIds(series) {
  if (!series) return { series, report: null };
  const canon = (divId, a, b) => `${divId || ''}|${[a || '', b || ''].sort().join('~')}`;
  const idByCanon = new Map();
  const stamp = Date.now();
  let counter = 0;
  const freshId = () => `pair_fix_${stamp}_${counter++}_${Math.random().toString(36).slice(2, 7)}`;

  const before = (series.pairs || []).length;
  let idsReassigned = 0,
    duplicatesRemoved = 0;

  // 1. Re-id + dedupe pairs (one unique id per division+player pair)
  const seen = new Set();
  const newPairs = [];
  (series.pairs || []).forEach((p) => {
    const c = canon(p.divisionId, p.p1Id, p.p2Id);
    if (!idByCanon.has(c)) idByCanon.set(c, freshId());
    const nid = idByCanon.get(c);
    if (seen.has(c)) {
      duplicatesRemoved++;
      return;
    }
    seen.add(c);
    if (nid !== p.id) idsReassigned++;
    newPairs.push({ ...p, id: nid });
  });

  // 2. Re-link matches via the players (canonical), not via the old ids
  let matchesRelinked = 0;
  const newMatches = (series.matches || []).map((m) => {
    const t1id = idByCanon.get(canon(m.divisionId, m.team1?.p1Id, m.team1?.p2Id));
    const t2id = idByCanon.get(canon(m.divisionId, m.team2?.p1Id, m.team2?.p2Id));
    let changed = false;
    const team1 =
      t1id && m.team1 && t1id !== m.team1.pairId
        ? ((changed = true), { ...m.team1, pairId: t1id })
        : m.team1;
    const team2 =
      t2id && m.team2 && t2id !== m.team2.pairId
        ? ((changed = true), { ...m.team2, pairId: t2id })
        : m.team2;
    if (changed) matchesRelinked++;
    return changed ? { ...m, team1, team2 } : m;
  });

  // 3. Update the division.pairs references in config.classes
  const newClasses = (series.config?.classes || []).map((cls) => ({
    ...cls,
    divisions: (cls.divisions || []).map((d) => ({
      ...d,
      pairs: (d.pairs || []).map((pp) => {
        const nid = idByCanon.get(canon(d.id, pp.p1Id, pp.p2Id));
        if (!nid) return pp;
        return {
          ...pp,
          ...(pp.id != null ? { id: nid } : {}),
          ...(pp.pairId != null ? { pairId: nid } : {}),
        };
      }),
    })),
  }));

  const repaired = {
    ...series,
    pairs: newPairs,
    matches: newMatches,
    config: { ...series.config, classes: newClasses },
  };
  return {
    series: repaired,
    report: {
      pairsBefore: before,
      pairsAfter: newPairs.length,
      duplicatesRemoved,
      idsReassigned,
      matchesRelinked,
    },
  };
}

// Local date formatting — NOT toISOString (UTC), which shifts the day in UTC+
// zones (DOMAIN_RULES §4). Module scope so startNextSeason's fallback uses the
// same formatter as serieExpandWeekdayDates (#939).
const fmtLocal = (dt: Date): string =>
  `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;

export function startNextSeason(
  series,
  classId,
  { assignments = null, newDeadline = null, newSeasonStart = null, newPairs = {} } = {},
  allPlayers = [],
) {
  if (!series || !classId) return series;
  const cls = (series.config?.classes || []).find((c) => c.id === classId);
  if (!cls) return series;

  const stByDiv = seriespillStandingsByDivision(series);
  const transition = proposeSeasonTransition(series, classId);

  // Default plan if not specified. Resolves pairs to {p1Id,p2Id} directly from
  // division-scoped standings — ROBUST against duplicated/colliding pair ids.
  let resolvedAssignment = assignments;
  if (!resolvedAssignment) {
    const divs = [...(cls.divisions || [])].sort((a, b) => (a.level || 0) - (b.level || 0));
    const goingUpByDiv = {},
      goingDownByDiv = {};
    (transition[cls.id]?.divisions || []).forEach((d) => {
      goingUpByDiv[d.id] = d.goingUp || [];
      goingDownByDiv[d.id] = d.goingDown || [];
    });
    // Look up a pair id ONLY in its own division's standings (not globally).
    const resolveInDiv = (divId, pid) => {
      const hit = (stByDiv[divId] || []).find((s) => s.pair?.id === pid);
      return hit?.pair ? { p1Id: hit.pair.p1Id, p2Id: hit.pair.p2Id } : null;
    };
    const classPlan = {};
    divs.forEach((d, idx) => {
      const above = divs[idx - 1],
        below = divs[idx + 1];
      const myStandings = stByDiv[d.id] || [];
      const goingUpFromMe = new Set(goingUpByDiv[d.id] || []);
      const goingDownFromMe = new Set(goingDownByDiv[d.id] || []);
      const stayers = myStandings
        .filter((s) => !goingUpFromMe.has(s.pair.id) && !goingDownFromMe.has(s.pair.id))
        .map((s) => ({ p1Id: s.pair.p1Id, p2Id: s.pair.p2Id }));
      const incomingFromAbove = above
        ? (goingDownByDiv[above.id] || []).map((pid) => resolveInDiv(above.id, pid)).filter(Boolean)
        : [];
      const incomingFromBelow = below
        ? (goingUpByDiv[below.id] || []).map((pid) => resolveInDiv(below.id, pid)).filter(Boolean)
        : [];
      classPlan[d.id] = [...stayers, ...incomingFromAbove, ...incomingFromBelow];
    });
    resolvedAssignment = classPlan;
  }

  // Build new divisions ONLY for this class
  // Include any NEW pairs (added in the season modal) — they are not in
  // series.pairs yet, but are resolved here so {p1Id,p2Id} is fetched for the next season.
  const pairById = new Map<any, any>(
    [...(series.pairs || []), ...Object.values(newPairs || {})].map((p: any) => [p.id, p]),
  );
  const nextSeasonNum = (cls.currentSeason || 1) + 1;
  const newDivPairs = [];
  const newDivMatches = [];
  const effectiveDoubleRound =
    cls.doubleRound != null ? cls.doubleRound : series.config?.doubleRound;
  const nextDivs = [...(cls.divisions || [])]
    .sort((a, b) => (a.level || 0) - (b.level || 0))
    .map((d, divIdx) => {
      const pairIds = resolvedAssignment[d.id] || [];
      const nextPairsRaw = pairIds
        .map((entry) => {
          // New robust form: pre-resolved {p1Id,p2Id}. Legacy: string id looked up globally.
          if (entry && typeof entry === 'object' && (entry.p1Id || entry.p2Id)) {
            return { p1Id: entry.p1Id, p2Id: entry.p2Id };
          }
          const p = pairById.get(entry);
          return p ? { p1Id: p.p1Id, p2Id: p.p2Id } : null;
        })
        .filter(Boolean);

      const divisionId = `${cls.id}_div${d.level || divIdx + 1}_s${nextSeasonNum}`;
      const builtPairs = buildPairsFromPlayers(
        nextPairsRaw.map((p) => ({ ...p, classId: cls.id, divisionId })),
        allPlayers,
      );
      const sortedPairs = sortPairsBySeed(builtPairs).map((p, i) => ({
        ...p,
        seed: p.seedOverride ?? i + 1,
        groupId: divisionId,
      }));
      const effRules = seriespillEffectiveRules(series.config, cls, d);
      const divisionMeta = {
        id: divisionId,
        classId: cls.id,
        name: d.name || `${d.level || divIdx + 1}. divisjon`,
        level: d.level || divIdx + 1,
        promote: d.promote ?? SERIESPILL_DEFAULT_PROMOTE,
        relegate: d.relegate ?? SERIESPILL_DEFAULT_RELEGATE,
        starpoint: !!d.starpoint,
        matchFormat: d.matchFormat ?? null,
        minutesPerMatch: d.minutesPerMatch || SERIESPILL_DEFAULT_MINUTES,
        pairs: sortedPairs,
      };
      const divMatches =
        sortedPairs.length >= 2
          ? buildSeriespillDivisionMatches(divisionMeta, sortedPairs, {
              doubleRound: !!effectiveDoubleRound,
              matchFormat: effRules.matchFormat,
            })
          : [];
      newDivPairs.push(...sortedPairs);
      newDivMatches.push(...divMatches);
      return divisionMeta;
    });

  // Save the class's previous season on its own seriesHistory
  const oldDivIds = new Set((cls.divisions || []).map((d) => d.id));
  const completedSeason = {
    season: cls.currentSeason || 1,
    endedAt: Date.now(),
    standings: Object.fromEntries(
      Object.entries(stByDiv).filter(([divId]) => oldDivIds.has(divId)),
    ),
    transition: transition[cls.id],
    matches: (series.matches || []).filter((m) => oldDivIds.has(m.divisionId)),
    pairs: (series.pairs || []).filter((p) => oldDivIds.has(p.divisionId)),
    divisions: cls.divisions || [],
  };

  // Build a new series: replace the class's divisions, remove old pairs/matches for the class,
  // insert the new ones, update the class state.
  const updatedClass = {
    ...cls,
    divisions: nextDivs,
    currentSeason: nextSeasonNum,
    seasonStatus: 'active',
    lockedAt: null,
    lockReason: null,
    // The fallback is the LOCAL today, not the UTC one (DOMAIN_RULES §4, #939).
    // Only reached when neither the caller nor the class supplies a start.
    seasonStart: newSeasonStart || cls.seasonStart || fmtLocal(new Date()),
    deadline: newDeadline || cls.deadline || null,
    seriesHistory: [...(cls.seriesHistory || []), completedSeason],
  };

  const remainingPairs = (series.pairs || []).filter((p) => !oldDivIds.has(p.divisionId));
  const remainingMatches = (series.matches || []).filter((m) => !oldDivIds.has(m.divisionId));

  return {
    ...series,
    config: {
      ...series.config,
      classes: (series.config.classes || []).map((c) => (c.id === classId ? updatedClass : c)),
    },
    pairs: [...remainingPairs, ...newDivPairs],
    matches: [...remainingMatches, ...newDivMatches],
    // Keep the league-level status for backwards-compat if ALL classes are active again
    seasonStatus: 'active',
    lockedAt: null,
    lockReason: null,
  };
}

// Regenerate matches for classes/divisions that have no matches yet (e.g. when
// admin has added pairs to an empty division afterwards). Returns a new series.
// Does NOT touch divisions that already have matches.
export function regenerateEmptyDivisionMatches(series, _allPlayers = []) {
  if (!series) return series;
  const existingMatchDivs = new Set((series.matches || []).map((m) => m.divisionId));
  const newMatches = [];
  let touched = false;
  (series.config?.classes || []).forEach((cls) => {
    const effectiveDoubleRound =
      cls.doubleRound != null ? cls.doubleRound : series.config?.doubleRound;
    (cls.divisions || []).forEach((div) => {
      if (existingMatchDivs.has(div.id)) return; // already has matches
      const dPairs = (series.pairs || []).filter((p) => p.divisionId === div.id);
      if (dPairs.length < 2) return; // still too few pairs
      const effRules = seriespillEffectiveRules(series.config, cls, div);
      const divMatches = buildSeriespillDivisionMatches(div, dPairs, {
        doubleRound: !!effectiveDoubleRound,
        matchFormat: effRules.matchFormat,
      });
      newMatches.push(...divMatches);
      touched = true;
    });
  });
  if (!touched) return series;
  return { ...series, matches: [...(series.matches || []), ...newMatches] };
}

// ---------- Close a round (forfeit unplayed matches) ----------
// Gives all unplayed matches in a division's round the configured result.
// policy: 'double_wo' (default) → both teams lose 0–2 (double WO), or
// 'void' → the match is recorded as a draw (counts as played, 0 points to each,
// kept out of ELO via the walkover flag). WO/draw is kept out of ELO (recalcKMElo skips walkover). Returns a new series.
export function endSeriespillRound(series, divisionId, round, opts: any = {}) {
  if (!series) return series;
  const cls = (series.config?.classes || []).find((c) =>
    (c.divisions || []).some((d) => d.id === divisionId),
  );
  const eff = cls ? seriespillEffectiveConfig(series, cls.id) : series.config;
  const policy = opts.policy || eff?.unplayedPolicy || 'double_wo';
  const now = Date.now();
  let changed = false;
  const matches = (series.matches || []).map((m) => {
    if (m.divisionId !== divisionId || m.round !== round || m.score) return m;
    changed = true;
    const score =
      policy === 'void'
        ? { t1: 0, t2: 0, draw: true, walkover: true }
        : { t1: 0, t2: 0, walkover: true, doubleWalkover: true };
    return { ...m, score, completed: true, completedAt: now, forfeited: true };
  });
  if (!changed) return series;
  return { ...series, matches };
}

// ---------- Round announcement (rolling reveal) ----------
// revealMode 'all' (default) = all rounds visible immediately. 'rolling' =
// future rounds are announced just before they start. A round is revealed if:
//   - it is active or played (round <= activeRound), OR
//   - it is within revealAhead rounds ahead of active, AND the lead time before start has been reached.
// revealLeadValue/Unit = how long before round start it is announced.
// revealAhead = how many rounds ahead you are (1 = only the next).
export function seriespillRoundRevealed(config, round, activeRound, now = Date.now()) {
  if (!config || config.revealMode !== 'rolling') return true;
  if (activeRound == null) return true; // season finished — show everything
  if (round <= activeRound) return true; // active or played
  if (Array.isArray(config.manualReveals) && config.manualReveals.includes(round)) return true; // manually announced
  const ahead = Math.max(1, Number(config.revealAhead) || 1);
  if (round - activeRound > ahead) return false; // too far ahead
  const startD = seriespillRoundDeadline(config, round - 1); // the round's start
  if (!startD) return true; // no cadence → show
  const dayMs = 86400000;
  const unit = config.revealLeadUnit === 'weeks' ? 7 : 1;
  const leadMs = (Number(config.revealLeadValue) || 0) * unit * dayMs;
  return now >= startD.getTime() - leadMs;
}

// The date a round is announced (round start minus lead time). null if unknown.
export function seriespillRoundRevealDate(config, round) {
  const startD = seriespillRoundDeadline(config, round - 1);
  if (!startD) return null;
  const dayMs = 86400000;
  const unit = config?.revealLeadUnit === 'weeks' ? 7 : 1;
  const leadMs = (Number(config?.revealLeadValue) || 0) * unit * dayMs;
  return new Date(startD.getTime() - leadMs);
}

// Find division rounds whose deadline has passed and that still have unplayed matches.
// Returns [{ divisionId, round }]. Used for auto-forfeit at the deadline.
export function seriespillOverdueRounds(series, now = Date.now()) {
  if (!series) return [];
  const out = [];
  (series.config?.classes || []).forEach((cls) => {
    if (cls.seasonStatus === 'locked') return;
    const eff = seriespillEffectiveConfig(series, cls.id);
    if (!eff.autoForfeitOverdue) return; // auto-forfeit off for this class
    (cls.divisions || []).forEach((div) => {
      const dm = (series.matches || []).filter((m) => m.divisionId === div.id);
      const byRound = {};
      dm.forEach((m) => {
        (byRound[m.round] = byRound[m.round] || []).push(m);
      });
      Object.keys(byRound)
        .map(Number)
        .forEach((round) => {
          const ms = byRound[round];
          if (!ms.some((m) => !m.score)) return; // everything played
          const rd = seriespillRoundDeadline(eff, round);
          if (rd && rd.getTime() < now) out.push({ divisionId: div.id, round });
        });
    });
  });
  return out;
}

// Replace an (injured) player in the seriespill. As in Padel24, only FUTURE
// matches are affected: already-played matches get a frozen `roster` so that the
// ELO history stays with the original player, while the substitute takes over
// the pair's remaining matches. Standings (the pair keeps its points) are
// unchanged. After this the caller must run recalcKMElo + save.
export function replacePlayerInSeries(tournament, originalId, replacementPlayer, allPlayers = []) {
  if (!tournament || !originalId || !replacementPlayer?.id) return tournament;
  const replId = replacementPlayer.id;
  const replName = replacementPlayer.name || 'Innbytter';
  const pairs = tournament.pairs || [];
  const pairById = new Map<any, any>(pairs.map((p) => [p.id, p]));
  const affected = new Set(
    pairs.filter((p) => p.p1Id === originalId || p.p2Id === originalId).map((p) => p.id),
  );
  if (affected.size === 0) return tournament;
  const eloOf = (pid) => {
    if (pid === replId) return replacementPlayer.elo ?? 1400;
    const pl = (allPlayers || []).find((x) => x.id === pid);
    return pl?.elo ?? 1400;
  };
  // 1) Freeze the roster on already-played matches (preserves the original in the ELO history)
  const matches = (tournament.matches || []).map((m) => {
    if (!m.score || m.roster) return m;
    const t1 = pairById.get(m.team1?.pairId);
    const t2 = pairById.get(m.team2?.pairId);
    if (!t1 || !t2) return m;
    if (!affected.has(m.team1?.pairId) && !affected.has(m.team2?.pairId)) return m;
    return { ...m, roster: { t1: [t1.p1Id, t1.p2Id], t2: [t2.p1Id, t2.p2Id] } };
  });
  // 2) Replace the player in the affected pairs (update name + eloSum for seeding/display)
  const newPairs = pairs.map((p) => {
    if (!affected.has(p.id)) return p;
    const p1Id = p.p1Id === originalId ? replId : p.p1Id;
    const p2Id = p.p2Id === originalId ? replId : p.p2Id;
    const p1Name = p.p1Id === originalId ? replName : p.p1Name;
    const p2Name = p.p2Id === originalId ? replName : p.p2Name;
    return { ...p, p1Id, p2Id, p1Name, p2Name, eloSum: eloOf(p1Id) + eloOf(p2Id) };
  });
  const replacements = [
    ...(tournament.replacements || []),
    { originalId, replacementId: replId, at: Date.now() },
  ];
  return { ...tournament, pairs: newPairs, matches, replacements };
}

// Resolves the current («effective») rules for a division: the league (config)
// is the base, the class can override, and the division fine-tunes. «First
// defined» rule: division ?? class ?? league ?? hard default. Legacy `starpoint`
// on a division is interpreted as a golden-point override when `goldenPoint` is
// not set.
// Labels + descriptions for the three deuce modes (reused in UI + rules).
export const SERIE_DEUCE = {
  advantage: {
    label: 'Fordel',
    sub: 'vanlig',
    desc: 'Fordel (advantage): Ved 40–40 må et par vinne to poeng på rad for å ta gamet. Taper de det andre poenget, går stillingen tilbake til 40–40.',
  },
  golden: {
    label: 'Gullpoeng',
    sub: '',
    desc: 'Gullpoeng (golden point): Allerede ved første 40–40 spilles ett avgjørende poeng — laget som vinner poenget tar gamet. Mottakende par velger hvilken side de tar imot serven på.',
  },
  starpoint: {
    label: 'Starpoint',
    sub: '',
    desc: 'Starpoint: De to første gangene det blir 40–40 i et game spilles normalt (fordel). Tredje gang det blir 40–40 spilles ett avgjørende gullpoeng. Mottakende par velger side på gullpoenget.',
  },
};
export const SERIE_DEUCE_ORDER = ['advantage', 'golden', 'starpoint'];

// Maps a level object (config/class/division) to a deuce mode or null (inherit).
// Backwards-compat: old goldenPoint boolean → 'golden' (true) / 'advantage' (false).
export function serieDeuceOf(o) {
  if (!o) return null;
  if (o.deuceMode === 'advantage' || o.deuceMode === 'golden' || o.deuceMode === 'starpoint')
    return o.deuceMode;
  if (o.goldenPoint === true) return 'golden';
  if (o.goldenPoint === false) return 'advantage';
  return null;
}

export function seriespillEffectiveRules(config, cls, div) {
  const fd = (...xs) => {
    for (const x of xs) if (x != null) return x;
    return undefined;
  };
  const deuceMode = fd(serieDeuceOf(div), serieDeuceOf(cls), serieDeuceOf(config), 'advantage');
  return {
    matchFormat: fd(
      div?.matchFormat,
      cls?.matchFormat,
      config?.matchFormat,
      SERIESPILL_DEFAULT_MATCH_FORMAT,
    ),
    deuceMode,
    goldenPoint: deuceMode === 'golden',
    mixServeRule: fd(div?.mixServeRule, cls?.mixServeRule, config?.mixServeRule, false),
  };
}

export function serieExpandWeekdayDates(startISO, endISO, weekdays) {
  const out = [];
  if (!startISO || !endISO || !Array.isArray(weekdays) || !weekdays.length) return out;
  const wd = new Set(weekdays.map(Number));
  const start = new Date(startISO + 'T00:00:00');
  const end = new Date(endISO + 'T00:00:00');
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return out;
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    if (wd.has(d.getDay())) out.push(fmtLocal(d));
  }
  return out;
}

// Builds reservations for all time slots: one booking per court × slot start time × date.
// Reuses expandRecurrence (weekly/biweekly via interval) + isHoliday filter.
// recurrence: 'weekly' | 'biweekly' | 'single'. skipHolidays skips public holidays.
// Runs validateBooking against existing + already-planned. Returns { toCreate, conflicts }.
// Generates all slots from the time slots (no validation, no storage).
// Returns [{date,startTime,endTime,court,classIds,roomName}]. Used both by
// reservation (integrated) and standalone placement + calendar view.
export function serieGenerateSlots(
  { rooms = [], slotDuration = 90, startISO, endISO },
  holidayCfg = null,
) {
  const out = [];
  const fmt = (mins) =>
    `${String(Math.floor((mins % (24 * 60)) / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
  const toMin = (t) => {
    const [h, m] = (t || '0:0').split(':').map(Number);
    return h * 60 + m;
  };
  (rooms || []).forEach((room) => {
    const recurring = room.recurring !== false;
    let dates;
    if (!recurring) {
      dates = room.singleDate ? [room.singleDate] : [];
    } else {
      const freq = room.recurFreq || 'weekly';
      const interval = Math.max(1, Number(room.recurInterval) || 1);
      const man0 = (room.weekdays || []).map((d) => (Number(d) + 6) % 7);
      const re = room.recurEnd || 'until';
      let endType = 'until',
        count,
        until = endISO;
      if (re === 'count') {
        endType = 'count';
        count = Math.max(1, Number(room.recurCount) || 1);
      } else if (re === 'until') {
        endType = 'until';
        until = room.recurUntil || endISO;
      } else {
        endType = 'until';
        until = endISO;
      }
      const recur = {
        freq,
        interval,
        weekdays: freq === 'weekly' ? man0 : [],
        endType,
        count,
        until,
      };
      dates = startISO ? expandRecurrence(startISO, recur) : [];
    }
    if ((room.holidayMode || 'skip') === 'skip' && holidayCfg)
      dates = dates.filter((iso) => !isHoliday(iso, holidayCfg));
    const count = Math.max(1, Number(room.slotCount) || 1);
    const startM = toMin(room.startTime);
    const courts = (room.courtList || []).map((c) => Number(c.num) || 0).filter((n) => n > 0);
    // Block mode (mini tournaments): one continuous time slot start→end per
    // court (not split into slots). Activated when the room has endTime set.
    const blockEndM = room.endTime ? toMin(room.endTime) : null;
    const blockMode = blockEndM != null && blockEndM > startM;
    dates.forEach((date) => {
      if (blockMode) {
        const startTime = fmt(startM),
          endTime = fmt(blockEndM),
          durationMin = blockEndM - startM;
        courts.forEach((court) =>
          out.push({
            date,
            startTime,
            endTime,
            durationMin,
            court,
            classIds: [...(room.classIds || [])],
            roomName: room.name || 'Tidsrom',
          }),
        );
      } else {
        for (let k = 0; k < count; k++) {
          const sM = startM + k * slotDuration;
          const startTime = fmt(sM),
            endTime = fmt(sM + slotDuration);
          courts.forEach((court) =>
            out.push({
              date,
              startTime,
              endTime,
              durationMin: slotDuration,
              court,
              classIds: [...(room.classIds || [])],
              roomName: room.name || 'Tidsrom',
            }),
          );
        }
      }
    });
  });
  return out;
}

export function serieBuildReservations(
  { rooms = [], slotDuration = 90, startISO, endISO, seriesId, seriesName = 'Seriespill' },
  existingBookings = [],
  bufferMin = BOOKING_DEFAULT_BUFFER_MIN,
  holidayCfg = null,
  adminOverride = false,
) {
  const toCreate = [];
  const conflicts = [];
  const running = [...existingBookings];
  const slots = serieGenerateSlots({ rooms, slotDuration, startISO, endISO }, holidayCfg);
  // ADMIN OVERRIDE: admin reserves tournament courts — dead gaps (smartbooking
  // violations) are allowed (shown in yellow as a warning). Only HARD overlaps are rejected then.
  const vOpts = adminOverride
    ? { bufferMin: 0, smartAllocation: false }
    : { bufferMin, smartAllocation: false };
  slots.forEach((sl) => {
    const candidate = {
      court: sl.court,
      date: sl.date,
      startTime: sl.startTime,
      endTime: sl.endTime,
    };
    const v = validateBooking(candidate, running, vOpts);
    if (v.ok) {
      const bk = {
        court: sl.court,
        date: sl.date,
        startTime: sl.startTime,
        endTime: sl.endTime,
        durationMin: sl.durationMin || slotDuration,
        title: `${seriesName} — ${sl.roomName}`,
        seriesId,
        classIds: sl.classIds,
        source: 'tournament',
        paymentStatus: 'free',
        priceNok: 0,
      };
      toCreate.push(bk);
      running.push({ ...bk, id: `plan_${toCreate.length}` });
    } else {
      conflicts.push({
        date: sl.date,
        court: sl.court,
        startTime: sl.startTime,
        endTime: sl.endTime,
        reason: v.reason || 'Opptatt',
      });
    }
  });
  return { toCreate, conflicts };
}

// Place matches into reserved slots — round by round, round 1 in the earliest
// slots. Slots: [{date,startTime,endTime,court,classIds?}]. A slot with empty
// classIds applies to all classes; otherwise only matches in the given classes.
// Skips a slot if one of the teams is already playing at the same date+time.
// Completed matches are left untouched. Returns { matches, placed, unplaced }.
export function serieAssignMatchesToSlots(matches, slots) {
  const sortedSlots = [...(slots || [])].sort(
    (a, b) =>
      (a.date || '').localeCompare(b.date || '') ||
      (a.startTime || '').localeCompare(b.startTime || '') ||
      (a.court || 0) - (b.court || 0),
  );
  const anyClass = sortedSlots.some((s) => !(s.classIds && s.classIds.length));
  const coveredClasses = new Set();
  sortedSlots.forEach((s) => (s.classIds || []).forEach((id) => coveredClasses.add(id)));
  const eligible = (m, s) =>
    s.classIds && s.classIds.length ? s.classIds.includes(m.classId) : true;
  // Incremental: slots that already have a placed match are taken; those teams
  // are busy at their date+time. We place ONLY matches without a date (unplaced).
  const occupiedSlot = new Set(); // `${date}|${startTime}|${court}`
  const busy = new Set(); // `${date}|${startTime}|${pairId}`
  (matches || []).forEach((m) => {
    if (m.date && m.startTime != null && m.court != null)
      occupiedSlot.add(`${m.date}|${m.startTime}|${m.court}`);
    if (m.date && m.startTime) {
      if (m.team1?.pairId) busy.add(`${m.date}|${m.startTime}|${m.team1.pairId}`);
      if (m.team2?.pairId) busy.add(`${m.date}|${m.startTime}|${m.team2.pairId}`);
    }
  });
  const used = sortedSlots.map((s) => occupiedSlot.has(`${s.date}|${s.startTime}|${s.court}`));
  const order = (matches || [])
    .map((m, i) => ({ m, i }))
    .filter(({ m }) => !m.completed && !m.date && (anyClass || coveredClasses.has(m.classId)))
    .sort(
      (a, b) =>
        a.m.round - b.m.round ||
        String(a.m.divisionId).localeCompare(String(b.m.divisionId)) ||
        a.i - b.i,
    );
  const assign = new Map(); // matchId -> slot
  order.forEach(({ m }) => {
    const t1 = m.team1?.pairId,
      t2 = m.team2?.pairId;
    for (let s = 0; s < sortedSlots.length; s++) {
      if (used[s]) continue;
      const sl = sortedSlots[s];
      if (!eligible(m, sl)) continue;
      if (
        busy.has(`${sl.date}|${sl.startTime}|${t1}`) ||
        busy.has(`${sl.date}|${sl.startTime}|${t2}`)
      )
        continue;
      used[s] = true;
      busy.add(`${sl.date}|${sl.startTime}|${t1}`);
      busy.add(`${sl.date}|${sl.startTime}|${t2}`);
      assign.set(m.id, sl);
      break;
    }
  });
  const attempted = order.length;
  const placed = assign.size;
  const assignedIds = new Set([...assign.keys()]);
  const updated = (matches || []).map((m) => {
    if (!assignedIds.has(m.id)) return m; // completed / already placed / not covered / no room found — leave as is
    const sl = assign.get(m.id);
    return { ...m, court: sl.court, date: sl.date, startTime: sl.startTime, endTime: sl.endTime };
  });
  return { matches: updated, placed, unplaced: attempted - placed };
}

// A league match can have court/date/time either directly (calendar placement) OR
// via a free-booking reservation (m.bookingInfo). These helpers give the
// effective court/date/time regardless of source — so the banepad finds reserved matches.
// Moved to @starpoint/engine (see padel24.ts); re-exported so existing imports
// keep working.
export { serieMatchCourt } from './time.js';
export function serieMatchDate(m) {
  return m?.date || m?.bookingInfo?.date || null;
}
export function serieMatchStart(m) {
  return m?.startTime || m?.bookingInfo?.startTime || null;
}
export function serieMatchEnd(m) {
  return m?.endTime || m?.bookingInfo?.endTime || null;
}
